import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import type { BankFindInterface } from '@zaoblako/shared/modules/catalog/dtos/bank/bank-find.dto';
import { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import type { CompanyGroupFindInterface } from '@zaoblako/shared/modules/client/dtos/company.group/company-group-find.dto';
import {
  CompanyFormDto,
  type CompanyFormInterface
} from '@zaoblako/shared/modules/client/dtos/company/company.form.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { PersonFormInterface } from '@zaoblako/shared/modules/client/dtos/person/person-form.dto';
import { PersonTypeEnum } from '@zaoblako/shared/modules/client/enums/person.type.enum';
import type { Connection1cFindInterface } from '@zaoblako/shared/modules/integration/dtos/connection-1c/connection-1c-find.dto';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { BankService } from '@zaoblako/web/core/services/catalog/bank.service';
import { CompanyGroupService } from '@zaoblako/web/core/services/client/company-group.service';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { Integration1cService } from '@zaoblako/web/core/services/integration/integration1c.service';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import { JurisdictionTypeEnum } from '@zaoblako/shared/modules/client/enums/jurisdiction-type.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { InnAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/inn-auto-complete/inn-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatabaseSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client/database-single-select/database-single-select.component';
import { TaxationSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client/taxation-single-select/taxation-single-select.component';
import { CurrencyMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/currency-multi-select/currency-multi-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TableBankDetailsComponent } from '@zaoblako/web/shared/dumbs/table-bank-details/table-bank-details.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { PersonTypeNamePipe } from '@zaoblako/web/shared/pipes/person-type-name.pipe';
import { JurisdictionTypeNamePipe } from '@zaoblako/web/shared/pipes/jurisdiction-type-name.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogService } from 'primeng/dynamicdialog';
import { InputMaskModule } from 'primeng/inputmask';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { forkJoin, Observable, Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-company-form',
  templateUrl: './company-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InnAutoCompleteComponent,
    JoinPipe,
    InputTextComponent,
    SingleSelectComponent,
    InputMaskModule,
    FormsModule,
    DatabaseSingleSelectComponent,
    ButtonModule,
    TooltipModule,
    TaxationSingleSelectComponent,
    TableBankDetailsComponent,
    PersonTypeNamePipe,
    SpinnerComponent,
    CheckboxModule,
    JurisdictionTypeNamePipe,
    CurrencyMultiSelectComponent,
    TableComponent
  ],
  providers: [DialogService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanyFormComponent extends BaseFormComponent<CompanyFormInterface> implements OnInit, OnDestroy {
  @Input() public readonly index: number;
  public company = <CompanyFormInterface>{};
  public dto = CompanyFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;
  public engine: 'class-transformer' | 'serde' = 'serde';

  public canDelete = false;
  public person: PersonFormInterface;

  public banks: BankFindInterface[] = [];
  public databases: Connection1cFindInterface[] = [];
  public groups: CompanyGroupFindInterface[] = [];
  public persons: PersonFormInterface[] = [];

  public personTypes: PersonTypeEnum[] = Object.values(PersonTypeEnum);
  public jurisdictions: JurisdictionTypeEnum[] = Object.values(JurisdictionTypeEnum);

  public columns: CrmTableColumn[] = [{ label: 'Валюта' }, { label: 'Валюта по-умолчанию' }];

  public get JurisdictionTypeEnum() {
    return JurisdictionTypeEnum;
  }

  private formSubscription$: Subscription = new Subscription();

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly integration1cService: Integration1cService,
    private readonly companyGroupService: CompanyGroupService,
    private readonly bankService: BankService,
    private readonly companyService: CompanyService,
    private readonly errorService: ErrorService,
    private readonly router: Router,
    private readonly confirmService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    forkJoin([
      this.integration1cService.find<Connection1cFindInterface>(),
      this.companyGroupService.find<CompanyGroupFindInterface>()
    ]).subscribe(([databases, groups]) => {
      this.databases = databases;
      this.groups = groups;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public ngOnDestroy() {
    this.formSubscription$?.unsubscribe();
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.companyService
      .findById<CompanyFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (company) => {
          this.company = company;
          if (this.company?.inn) {
            this.companyService.externalSearch({ query: this.company.inn }).subscribe((persons) => {
              this.person = persons[0] ?? <PersonFormInterface>{ inn: this.company.inn };
              this.cdRef.markForCheck();
            });
          }
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveCompany() {
    if (this.checkValidate(this.company).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.companyService.update<CompanyFormInterface, CompanyFindInterface>(this.company).subscribe({
          next: (company) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Компания "${this.company?.name}" обновлена`);
            this.formStoreService.saveNext(company, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.companyService.create<CompanyFindInterface>(this.company).subscribe({
          next: (company) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Компания создана');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/company/my/card/' + company.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(company, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteCompany() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить компанию "${this.company?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.companyService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Компания "${this.company?.name}" удалена`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
            this.cdRef.markForCheck();
          }
        });
      }
    });
  }

  public searchBank(name: string) {
    this.bankService.search({ search: name }).subscribe((banks) => {
      this.banks = banks;
      this.cdRef.markForCheck();
    });
  }

  public searchParson(name: string) {
    this.companyService.externalSearch({ query: name }).subscribe((persons) => {
      this.persons = persons;
      this.cdRef.markForCheck();
    });
  }

  public selectedPerson(person: PersonFormInterface) {
    if (person) {
      if (typeof person === 'object') {
        this.company = {
          ...this.company,
          name: person.name,
          personType: person.personType,
          manager: person.manager,
          email: person.email,
          phone: person.phone,
          website: person.website,
          address: person.address,
          postAddress: person.postAddress,
          kpp: person.kpp,
          ogrn: person.ogrn,
          inn: person.inn
        };
      } else {
        this.company.inn = person;
      }
    }
  }

  public showDialogCreate1C() {
    const result = this.formStoreService.addForm<Connection1cFindInterface>(TypeFormEnum.DATABASE_1C, {
      headerText: 'Создание базы 1С',
      widthSize: 'form-sidebar-small',
      isNotShowRedirectInCreate: true
    });

    this.formSubscription$.add(
      result.onSave.subscribe((database) => {
        this.databases.push(database);
        this.databases = [...this.databases];
        this.cdRef.markForCheck();
      })
    );
  }

  public toCurrency(currency: any): CurrencyFindInterface {
    return currency as CurrencyFindInterface;
  }

  public setDefaultCurrency(currency: CurrencyFindInterface, value: boolean) {
    this.company.currencies = this.company.currencies.map((currency) => ({ ...currency, isDefault: false }));
    this.company.currencies.find((_currency) => _currency?.id === currency?.id).isDefault = value;
  }

  public selectCurrencies() {
    if (this.company.currencies?.length) {
      this.company.currencies = this.company.currencies.map((currency) => ({ ...currency, isDefault: false }));
      if (this.company.currencies.length === 1) {
        this.company.currencies[0].isDefault = true;
      }
    }
  }
}
